import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  ReferenceLine,
} from "recharts";
import styles from "@/page-components/Cost-Anomaly/anomaly.module.css";
import {
  useIndividualGraphData,
  useIndividualStatsData,
} from "@/services/cost-anomaly/get-individual-graph-all-cloud.repo";
import CurrencyFormatter from "@/lib/value-converison";
import VerticalBarGraph from "@/components/States/verticalBarErrorState";
import config from "@/config";
import ScenariosErrorState from "@/components/States/scenariosErrorState";
import awsLogo from "@/assets/img/aws_logo.svg";
import azureLogo from "@/assets/img/azurelogo.svg";
import gcpLogo from "@/assets/img/gcp_logo.svg";
import idleStyles from "@/page-components/idle-resource/idle.module.css";

const AllCloudIndividualGraph = ({ service }) => {
  const query = useMemo(() => {
    return {
      service,
    };
  }, [service]);

  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useIndividualStatsData(query);

  const {
    data: graphData,
    isLoading: graphLoading,
    error: graphError,
  } = useIndividualGraphData(query);

  const combinedData = Object.keys(statsData || {})
    .filter((cloudKey) => config.enabledClouds.has(cloudKey))
    .map((cloudKey) => ({
      cloud: cloudKey.toUpperCase(),
      total: graphData?.[cloudKey]?.totalAnomalies || 0,
      spike: statsData?.[cloudKey]?.maxSpike || 0,
      cost: statsData?.[cloudKey]?.maxAnomalyCost || 0,
      threshold: statsData?.[cloudKey]?.totalAnomalyCost || 0,
      data: graphData?.[cloudKey]?.chartData || [],
    }));

  return (
    <div className={styles.graph_heading}>
      {((!statsLoading && !graphLoading && statsData && graphData) ||
        (statsError && graphError)) && (
        <div className={styles.heading_days}>
          <span className={styles.heading}>
            Individual Cloud Costs Anomalies
          </span>
          <span className={styles.last_days}> Last 90 Days</span>
        </div>
      )}
      {statsLoading && graphLoading && (
        <div
          className="container-fluid"
          style={{
            marginBottom: "7%",
            marginRight: "-12px",
            display: "flex",
            justifyContent: "start",
            gap: "20px",
            top: 16,
          }}
          data-testid="vertical-bar-graph"
        >
          {Array.from(config.enabledClouds).map((cloud, index) => (
            <VerticalBarGraph key={index} cloud={cloud} />
          ))}
        </div>
      )}
      {statsError && graphError && (
        <ScenariosErrorState
          error={graphError.message || statsError.message}
          messageConfig={{}}
        />
      )}
      {statsData && graphData && (
        <div className={styles.dashboard}>
          {combinedData.map((cloud, idx) => (
            <AnomalyCard
              key={idx}
              title={cloud.cloud}
              total={cloud.total}
              spike={cloud.spike}
              cost={cloud.cost}
              threshold={cloud.threshold}
              data={cloud.data}
            />
          ))}
        </div>
      )}
    </div>
  );
};
export default AllCloudIndividualGraph;

const AnomalyCard = ({ title, total, spike, cost, threshold, data }) => {
  const barColors = [
    "#E7C067",
    "#DF8367",
    "#7FBAFF",
    "#9D626E",
    "#6EA57C",
    "#CED4DA",
  ];

  return (
    <div className={styles.card}>
      <span className={styles.title}>{title} Anomalies</span>
      <span className={styles.total}>{total}</span>
      <p className={styles.spike}>
        Max spike{" "}
        <span>
          {spike.toFixed(2)}% • <CurrencyFormatter value={cost || 0} />
        </span>
      </p>

      {/* Bar Chart */}
      <div style={{ marginTop: "12px" }}>
        <ResponsiveContainer width="100%" height={175}>
          <BarChart
            barSize={16}
            data={data}
            layout="vertical"
            margin={{ top: 0, right: 15, bottom: 10, left: -16 }}
          >
            <CartesianGrid strokeDasharray="0" stroke="#F0F0F0" />
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              stroke="#A3A3A3"
              fontSize={10}
              fontWeight={400}
              fontFamily="Helvetica"
              // tickFormatter={(value) => `$${value}`}
            />
            <YAxis
              // type="category"
              dataKey="name"
              type="category"
              axisLine={false}
              tickLine={false}
              width={120}
              fontSize={10}
              fontWeight={400}
              fontFamily="Helvetica"
              color="#495057"
            />
            <Tooltip
              content={(props) => customIndividualTooltip({...props, title})}
              cursor={{ fill: "transparent" }}
            />
            <Bar dataKey="value">
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    entry.name === "Others"
                      ? "#9CA3AF"
                      : barColors[index % barColors.length]
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const customIndividualTooltip = ({ active, payload, label, title }) => {
  if (!active || !payload || payload.length === 0) return null;

  let logo = null;
  if (title?.toLowerCase() === "aws") logo = awsLogo;
  if (title?.toLowerCase() === "azure") logo = azureLogo;
  if (title?.toLowerCase() === "gcp") logo = gcpLogo;

  return (
    <div className={idleStyles.custom_tooltip}>
      <div className={idleStyles.tooltip_date} style={{ paddingLeft: "8px" }}>
        
        <div
          style={{
            display: "flex",
            justifyContent:"flex-end",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            paddingRight:"16px"
          }}
        >
          {logo && (
            <img
              src={logo}
              alt={`${title} logo`}
              className={idleStyles.cloud_logo}
            />
          )}
        </div>
      </div>

      <div style={{ paddingRight: "12px", width: "100%", paddingLeft: "8px" }}>
        <table>
          <tbody>
            {payload.map((entry, index) => (
              <tr
                key={`${idleStyles.tooltip_row}-${index}`}
                className={idleStyles.horizontalline_tool}
              >
                <td
                  className={idleStyles.cloud_cell}
                  style={{ paddingRight: "24px" }}
                >
                  {entry?.payload?.name}
                </td>
                <td
                  className={idleStyles.cost_cell}
                  style={{ paddingRight: "24px" }}
                >
                  {entry?.value || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
